import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useNoBarcodeVariants, useUnpooledListings } from '@/hooks/useAdmin'
import { useLatestSyncStatus } from '@/hooks/useSyncInventory'
import { fmtDateTime } from '@/lib/dates'
import type { SyncStatusResponse } from '@/lib/api/session11Types'

const isWarned = (latest: SyncStatusResponse | undefined) =>
  latest?.status === 'success' && !!latest.error_message?.startsWith('WARNING:')

// Failed in status red; a success that carries a "WARNING: …" message (e.g.
// sales zeroing skipped) in amber. The message itself is in SyncMessage.
function LastSync({ latest }: { latest: SyncStatusResponse | undefined }) {
  if (!latest?.status) return <span className="text-xs text-[var(--mute)]">Last sync: —</span>
  const when = latest.status === 'in_progress'
    ? `In progress since ${fmtDateTime(latest.started_at, '—')}`
    : fmtDateTime(latest.finished_at, '—')
  const warned = isWarned(latest)
  const statusColour = latest.status === 'failed'
    ? 'text-[var(--status-rejected-fg)]'
    : warned ? 'text-[var(--status-awaiting-fg)]' : ''
  const statusLabel = warned ? 'success with warnings' : latest.status.replace('_', ' ')
  return (
    <span className="text-xs text-[var(--mute)]" data-testid="last-sync">
      Last sync: {when}
      {latest.mode ? ` · ${latest.mode}` : ''}
      {' · '}
      <span className={statusColour}>{statusLabel}</span>
    </span>
  )
}

// The failure or warning text, visible and selectable: a hover tooltip
// vanished before it could be read or copied and never showed on touch.
// Warnings arrive as "WARNING: a; b; c" — one line each.
function SyncMessage({ latest }: { latest: SyncStatusResponse | undefined }) {
  const msg = latest?.error_message
  if (!msg) return null
  const warned = isWarned(latest)
  if (!warned && latest?.status !== 'failed') return null
  const items = warned ? msg.replace(/^WARNING:\s*/, '').split('; ') : [msg]
  return (
    <ul
      className={`text-xs [overflow-wrap:anywhere] ${
        warned ? 'text-[var(--status-awaiting-fg)]' : 'text-[var(--status-rejected-fg)]'
      }`}
      data-testid="sync-message"
    >
      {items.map((m, i) => <li key={i}>{m}</li>)}
    </ul>
  )
}

// Execution order of the sync phases (backend _PHASE_ORDER; full mode runs
// locations/products/variant-counts/costs, delta runs changed-products, never both).
const PHASE_ORDER = [
  'changed-products', 'locations', 'products', 'variant-counts', 'costs',
  'levels', 'incoming', 'orders', 'parallel-fetch', 'compute', 'write',
] as const
const PHASE_LABEL: Record<string, string> = {
  'changed-products': 'changed products',
  'variant-counts': 'variant check',
  'parallel-fetch': 'fetch (parallel)',
}

type PhaseTimings = {
  phases?: Record<string, number>
  requests?: number
  rate_limited_429s?: number
  graphql_throttled?: number
  parallel?: boolean
}

// Where the last sync spent its time and whether Shopify slowed it down —
// the owner can't read server logs. Older rows have no timings: no line.
function SyncPhases({ latest }: { latest: SyncStatusResponse | undefined }) {
  const t = latest?.phase_timings as PhaseTimings | null | undefined
  if (!t) return null
  const phases = t.phases ?? {}
  const parts = PHASE_ORDER
    .filter((n) => typeof phases[n] === 'number')
    .map((n) => `${PHASE_LABEL[n] ?? n} ${phases[n].toFixed(1)}s`)
  if (t.parallel) parts.push('fetched in parallel')
  if (typeof t.requests === 'number') parts.push(`${t.requests} requests`)
  const r429 = t.rate_limited_429s ?? 0
  const throttled = t.graphql_throttled ?? 0
  const slow = r429 + throttled
  return (
    <div className="text-[11px] text-[var(--mute)] tabular-nums text-right" data-testid="sync-phases">
      {parts.map((p) => `${p} · `).join('')}
      <span
        className={slow > 0 ? 'text-[var(--status-awaiting-fg)]' : undefined}
        title={slow > 0 ? `${r429} REST 429 / ${throttled} GraphQL throttled` : undefined}
      >
        Shopify slow-downs: {slow}
      </span>
    </div>
  )
}

// Products whose non-size options (Color, Style, Weight …) split their
// product-level lifetime figures into one pool per value. The owner asked to
// see these rather than have them pooled across options silently (L1b).
function UnpooledListings() {
  const { data: listings = [], isLoading } = useUnpooledListings()
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 flex flex-col gap-2" data-testid="unpooled-listings">
      <div className="flex items-center gap-3">
        <button onClick={() => setOpen((x) => !x)} className="flex items-center gap-1 text-xs font-medium hover:text-[var(--brand)]" aria-expanded={open}>
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Listings not pooled across options
        </button>
        <span className="px-2 py-0.5 text-xs border border-[var(--line-warm)] rounded">{isLoading ? '…' : listings.length}</span>
      </div>
      <div className="text-xs text-[var(--mute)]">
        The dashboard's product-level figures pool a product's sizes, but keep each colour, style or other option apart.
        These products are split that way.
      </div>
      {open && (
        <div className="overflow-auto max-h-72 border-t border-[var(--line-warm)] pt-2">
          {listings.length === 0 ? (
            <div className="text-xs text-[var(--mute)] p-2">No listings are split by options.</div>
          ) : (
            <table className="text-xs w-full">
              <thead className="text-left text-[var(--mute)] bg-[var(--surface-header)]">
                <tr>
                  <th className="tbl-th">Product</th>
                  <th className="tbl-th">Vendor</th>
                  <th className="tbl-th">Options</th>
                  <th className="tbl-th text-right">Pools</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((p, i) => (
                  <tr
                    key={p.product_id}
                    className={`border-t border-[var(--line-warm)] ${
                      i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                    }`}
                  >
                    <td className="px-2 py-1">{p.product_title ?? '—'}</td>
                    <td className="px-2 py-1 text-[var(--ink-2)]">{p.vendor ?? '—'}</td>
                    <td className="px-2 py-1">{p.option_names.join(', ')}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{p.pools}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

export function DataHealthSection() {
  const { data: variants = [], isLoading } = useNoBarcodeVariants()
  // Fetched once on mount (no polling); a finished sync invalidates it.
  const { data: latest } = useLatestSyncStatus()
  const [open, setOpen] = useState(false)

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">Data Health</h2>
      <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => setOpen((x) => !x)} className="flex items-center gap-1 text-xs font-medium hover:text-[var(--brand)]" aria-expanded={open}>
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Variants missing barcode
          </button>
          <span className="px-2 py-0.5 text-xs border border-[var(--line-warm)] rounded">{isLoading ? '…' : variants.length}</span>
          <div className="flex-1" />
          <LastSync latest={latest} />
        </div>
        <SyncMessage latest={latest} />
        <SyncPhases latest={latest} />
        {open && (
          <div className="overflow-auto max-h-72 border-t border-[var(--line-warm)] pt-2">
            {variants.length === 0 ? (
              <div className="text-xs text-[var(--mute)] p-2">All variants have barcodes. ✓</div>
            ) : (
              <table className="text-xs w-full">
                <thead className="text-left text-[var(--mute)] bg-[var(--surface-header)]">
                  <tr>
                    <th className="tbl-th">Variant ID</th>
                    <th className="tbl-th">SKU</th>
                    <th className="tbl-th">Product</th>
                    <th className="tbl-th">Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v, i) => (
                    <tr
                      key={v.variant_id}
                      className={`border-t border-[var(--line-warm)] ${
                        i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                      }`}
                    >
                      <td className="px-2 py-1 tabular-nums">{v.variant_id}</td>
                      <td className="px-2 py-1">{v.sku ?? '—'}</td>
                      <td className="px-2 py-1">{v.product_title ?? '—'}</td>
                      <td className="px-2 py-1 text-[var(--ink-2)]">{v.vendor ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <UnpooledListings />
    </section>
  )
}
